export const modifySceneCreditsPerSecond = {
  "480P": 3,
  "768P": 4,
} as const;

export const creditsForModifyScene = (
  duration: number,
  resolution: "480P" | "768P" = "768P",
): number =>
  Math.max(1, duration * (modifySceneCreditsPerSecond[resolution] ?? 4));

export const freeTierQuota = 20;
export const freeTierMessageLimit = 50;

export const messageLimitForPrice = (priceUSD: number): number =>
  freeTierMessageLimit + Math.floor(priceUSD / 5) * 200;

export type PricingTier = {
  id: "50_credits" | "150_credits" | "500_credits";
  name: string;
  priceUSD: number;
  credits: number;
  messageLimit: number;
  stripePriceId: string;
  popular?: boolean;
};

export const pricingTiers: readonly PricingTier[] = [
  {
    id: "50_credits",
    name: "50 Credits",
    priceUSD: 5,
    credits: 50,
    messageLimit: messageLimitForPrice(5),
    stripePriceId: "price_1TgZ70HiG8UAKPQvpQWNw4OV",
  },
  {
    id: "150_credits",
    name: "150 Credits",
    priceUSD: 10,
    credits: 150,
    messageLimit: messageLimitForPrice(10),
    stripePriceId: "price_1TgZ7IHiG8UAKPQvUr1I0xe5",
    popular: true,
  },
  {
    id: "500_credits",
    name: "500 Credits",
    priceUSD: 20,
    credits: 500,
    messageLimit: messageLimitForPrice(20),
    stripePriceId: "price_1TgZ7UHiG8UAKPQve4Vgog5U",
  },
] as const;

export type PricingTierId = typeof pricingTiers[number]["id"];

export const stripePriceIdMap: Record<PricingTierId, string> = {
  "50_credits": pricingTiers[0].stripePriceId,
  "150_credits": pricingTiers[1].stripePriceId,
  "500_credits": pricingTiers[2].stripePriceId,
};

export const defaultPriceId = stripePriceIdMap["50_credits"];

export const tier50Quota = pricingTiers[0].credits;
export const tier150Quota = pricingTiers[1].credits;
export const tier500Quota = pricingTiers[2].credits;

export const tier50MessageLimit = pricingTiers[0].messageLimit;
export const tier150MessageLimit = pricingTiers[1].messageLimit;
export const tier500MessageLimit = pricingTiers[2].messageLimit;

export const pricingPlansSummaryText = pricingTiers
  .map(
    (tier) =>
      `• $${tier.priceUSD}/month — ${tier.credits} credits (${tier.messageLimit} messages/month)${
        tier.popular ? " (Popular)" : ""
      }`,
  )
  .join("\n");

export const quotaForPrice = (priceUSD: number): number =>
  priceUSD >= 20
    ? pricingTiers[2].credits +
      Math.floor((priceUSD - 20) / 20) * pricingTiers[2].credits
    : priceUSD >= 10
    ? pricingTiers[1].credits
    : priceUSD >= 5
    ? pricingTiers[0].credits
    : priceUSD > 0
    ? Math.round(priceUSD * 10)
    : freeTierQuota;

export const monthInMs = 30 * 24 * 60 * 60 * 1000;

export type PaymentEvent = {
  type: string;
  amountUSD: number;
  timestamp: number;
};

const recentEvents = (events: PaymentEvent[]) =>
  events.filter(({ timestamp }) => timestamp > Date.now() - monthInMs);

const latestPatreonAmount = (events: PaymentEvent[]) =>
  recentEvents(events)
    .filter(({ type }) => type === "patreon")
    .sort((a, b) => b.timestamp - a.timestamp)[0]?.amountUSD ?? 0;

const latestStripeAmount = (events: PaymentEvent[]) =>
  recentEvents(events)
    .filter(({ type }) => type === "stripe")
    .sort((a, b) => b.timestamp - a.timestamp)[0]?.amountUSD ?? 0;

const sumByType = (type: string) => (events: PaymentEvent[]) =>
  recentEvents(events)
    .filter((e) => e.type === type)
    .reduce((sum, { amountUSD }) => sum + amountUSD, 0);

export const totalPaidAmount = (events: PaymentEvent[]): number =>
  latestPatreonAmount(events) +
  latestStripeAmount(events) +
  sumByType("crypto")(events);

export const computeQuota = (events: PaymentEvent[]): number =>
  quotaForPrice(totalPaidAmount(events)) +
  Math.round(sumByType("credit")(events) * 10);

export const isProFromEvents = (events: PaymentEvent[]): boolean =>
  recentEvents(events).some(({ amountUSD, type }) =>
    amountUSD > 0 && type !== "credit"
  );

export const recentUsageCount = (usageTimes: number[]): number =>
  usageTimes.filter((t) => t > Date.now() - monthInMs).length;

export const computeQuotaLeft = (
  events: PaymentEvent[],
  usageTimes: number[],
): number => Math.max(0, computeQuota(events) - recentUsageCount(usageTimes));

export const nextCreditReturnMs = (
  usageTimes: number[],
  quota: number,
): number | null => {
  const recent = usageTimes.filter((t) => t > Date.now() - monthInMs);
  if (recent.length === 0) return null;
  const sorted = [...recent].sort((a, b) => a - b);
  const overBy = recent.length - quota;
  const target = sorted[Math.max(0, overBy)];
  return target + monthInMs - Date.now();
};

export const computeMessageLimit = (events: PaymentEvent[]): number =>
  messageLimitForPrice(totalPaidAmount(events));
